import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  NamedLink,
  LayoutComposer,
  Heading,
} from '../../components';

import StaticPage from '../PageBuilder/StaticPage';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import BlogCard from '../../components/BlogCard/BlogCard';
import { useConfiguration } from '../../context/configurationContext';

import {
  fetchBlogPosts,
  fetchPillarPages,
  getBlogPosts,
  getPillarPages,
  getBlogPagination,
  getBlogFetchInProgress,
  getBlogFetchError,
  getPillarFetchInProgress,
  getPillarFetchError,
} from './BlogListPage.duck';

import css from './BlogListPage.module.css';

const FEATURED_PILLAR_SLUGS = [
  'camera-securite',
  'serrure-connectee',
  'thermostat-connecte',
  'ampoule-connectee',
  'robot-tondeuse',
  'sonnette-video',
];

const INITIAL_PILLAR_COUNT = 3;

export const BlogListPageComponent = props => {
  const {
    posts = [],
    pillarPages = [],
    pagination = {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
    fetchInProgress = false,
    fetchError = null,
    pillarFetchInProgress = false,
    pillarFetchError = null,
    fetchBlogPosts,
    fetchPillarPages,
    location,
    history,
  } = props;

  const intl = useIntl();
  const config = useConfiguration();
  const currentUser = useSelector(state => state.user.currentUser);
  const isAdmin = currentUser?.attributes?.profile?.publicData?.userType === 'admin';

  const [searchValue, setSearchValue] = React.useState('');
  const [showAllPillars, setShowAllPillars] = React.useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const page = parseInt(queryParams.get('page') || '1', 10);
    const category = queryParams.get('category');
    const tag = queryParams.get('tag');
    const search = queryParams.get('search');

    setSearchValue(search || '');

    if (config && config.marketplaceRootURL) {
      fetchBlogPosts({ page, category, tag, search }, config);
    }
  }, [location.search, fetchBlogPosts, config?.marketplaceRootURL]);

  useEffect(() => {
    if (!config?.marketplaceRootURL) return;
    if (pillarPages.length > 0) return;
    if (pillarFetchInProgress) return;
    if (pillarFetchError) return;
    fetchPillarPages({ limit: 48 }, config);
  }, [config?.marketplaceRootURL]);
  

  const layoutAreas = `
    topbar
    main
    footer
  `;

  const marketplaceRootURL = config?.marketplaceRootURL || '';
  const baseBlogUrl = `${marketplaceRootURL}/blog`;
  const currentPage = pagination?.page || 1;
  const totalPages = pagination?.totalPages || 1;
  const currentUrl = `${baseBlogUrl}${location.search || ''}`;

  const hasPreviousPage = currentPage > 1;
  const hasNextPage = currentPage < totalPages;

  const prevPageUrl = hasPreviousPage
    ? `${baseBlogUrl}?page=${currentPage - 1}`
    : null;
  const nextPageUrl = hasNextPage
    ? `${baseBlogUrl}?page=${currentPage + 1}`
    : null;

  const itemListSchema =
    posts && posts.length > 0 && marketplaceRootURL
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: posts.map((post, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'BlogPosting',
              headline: post.title,
              url: `${marketplaceRootURL}/blog/${post.slug}`,
              datePublished: post.published_at || null,
            },
          })),
        }
      : null;

  const onSubmitSearch = e => {
    e.preventDefault();
    const next = new URLSearchParams(location.search);
    const trimmed = (searchValue || '').trim();

    if (trimmed) {
      next.set('search', trimmed);
      next.set('page', '1');
    } else {
      next.delete('search');
      next.set('page', '1');
    }

    if (history && typeof history.push === 'function') {
      history.push({ search: `?${next.toString()}` });
    }
  };

  const onClearSearch = () => {
    setSearchValue('');
    const next = new URLSearchParams(location.search);
    next.delete('search');
    next.set('page', '1');
    if (history && typeof history.push === 'function') {
      history.push({ search: `?${next.toString()}` });
    }
  };

  const sortedPillarPages = React.useMemo(() => {
    if (!pillarPages || pillarPages.length === 0) return [];
    const slugIndex = {};
    FEATURED_PILLAR_SLUGS.forEach((s, i) => { slugIndex[s] = i; });
    return [...pillarPages].sort((a, b) => {
      const ai = slugIndex[a.slug] ?? Infinity;
      const bi = slugIndex[b.slug] ?? Infinity;
      return ai - bi;
    });
  }, [pillarPages]);

  const visiblePillars = showAllPillars
    ? sortedPillarPages
    : sortedPillarPages.slice(0, INITIAL_PILLAR_COUNT);
  const hasMorePillars = sortedPillarPages.length > INITIAL_PILLAR_COUNT;

  if (fetchError) {
    return (
      <StaticPage
        className={css.root}
        title={intl.formatMessage({ id: 'BlogListPage.title' })}
        scrollingDisabled={false}
      >
        <LayoutComposer areas={layoutAreas} className={css.layout}>
          {() => (
            <>
              <TopbarContainer />
              <main className={css.content}>
                <div className={css.error}>
                  <h2>
                    <FormattedMessage id="BlogListPage.errorTitle" />
                  </h2>
                  <p>{fetchError.message}</p>
                </div>
              </main>
              <FooterContainer />
            </>
          )}
        </LayoutComposer>
      </StaticPage>
    );
  }

  return (
    <>
      <Helmet>
        <title>{intl.formatMessage({ id: 'BlogListPage.title' })}</title>
        <meta
          name="description"
          content={intl.formatMessage({ id: 'BlogListPage.description' })}
        />
        {marketplaceRootURL ? (
          <>
            <link rel="canonical" href={currentUrl} />
            {hasPreviousPage && <link rel="prev" href={prevPageUrl} />}
            {hasNextPage && <link rel="next" href={nextPageUrl} />}
            {/* Open Graph */}
            <meta
              property="og:title"
              content={intl.formatMessage({ id: 'BlogListPage.title' })}
            />
            <meta
              property="og:description"
              content={intl.formatMessage({ id: 'BlogListPage.description' })}
            />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={currentUrl} />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary" />
            <meta
              name="twitter:title"
              content={intl.formatMessage({ id: 'BlogListPage.title' })}
            />
            <meta
              name="twitter:description"
              content={intl.formatMessage({ id: 'BlogListPage.description' })}
            />
          </>
        ) : null}
      </Helmet>

      <StaticPage
        className={css.root}
        title={intl.formatMessage({ id: 'BlogListPage.title' })}
        description={intl.formatMessage({ id: 'BlogListPage.description' })}
        scrollingDisabled={false}
        schema={itemListSchema}
      >
        <LayoutComposer areas={layoutAreas} className={css.layout}>
          {() => (
            <>
              <TopbarContainer />
              <main className={css.content}>
                <div className={css.header}>
                  <Heading as="h1" rootClassName={css.pageTitle}>
                    {intl.formatMessage({ id: 'BlogListPage.title' })}
                  </Heading>
                  <p className={css.subtitle}>
                    {intl.formatMessage({ id: 'BlogListPage.subtitle' })}
                  </p>

                  <form className={css.searchForm} onSubmit={onSubmitSearch}>
                    <label className={css.srOnly} htmlFor="blogSearch">
                      <FormattedMessage id="BlogListPage.searchLabel" />
                    </label>
                    <input
                      id="blogSearch"
                      type="search"
                      className={css.searchInput}
                      placeholder={intl.formatMessage({ id: 'BlogListPage.searchPlaceholder' })}
                      value={searchValue}
                      onChange={e => setSearchValue(e.target.value)}
                    />
                    {searchValue ? (
                      <button
                        type="button"
                        className={css.clearButton}
                        onClick={onClearSearch}
                      >
                        <FormattedMessage id="BlogListPage.clearSearch" />
                      </button>
                    ) : null}
                    <button type="submit" className={css.searchButton}>
                      <FormattedMessage id="BlogListPage.searchCta" />
                    </button>
                  </form>

                  {isAdmin && (
                    <div className={css.adminButtonContainer}>
                      <NamedLink name="BlogAdminPage" className={css.adminButton}>
                        <FormattedMessage id="BlogListPage.admin" />
                      </NamedLink>
                    </div>
                  )}
                </div>

                <section className={css.section}>
                  <div className={css.sectionHeader}>
                    <Heading as="h2" rootClassName={css.sectionTitle}>
                      <FormattedMessage id="BlogListPage.latestTitle" />
                    </Heading>
                    <p className={css.sectionSubtitle}>
                      <FormattedMessage id="BlogListPage.latestSubtitle" />
                    </p>
                  </div>

                {fetchInProgress && posts.length === 0 ? (
                  <div className={css.loading}>
                    <FormattedMessage id="BlogListPage.loading" />
                  </div>
                ) : (
                  <>
                    {posts.length > 0 && (
                      <div className={css.articlesGrid}>
                        {posts.map(post => {
                          if (!post || !post.id) return null;
                          return <BlogCard key={post.id} post={post} />;
                        })}
                      </div>
                    )}

                    {pagination && pagination.totalPages > 1 && (
                      <div className={css.pagination}>
                        {pagination.page > 1 && (
                          <NamedLink
                            name="BlogListPage"
                            to={{ search: `?page=${pagination.page - 1}` }}
                            className={css.paginationButton}
                          >
                            <FormattedMessage id="BlogListPage.previous" />
                          </NamedLink>
                        )}
                        <span className={css.paginationInfo}>
                          <FormattedMessage
                            id="BlogListPage.pageInfo"
                            values={{
                              current: pagination.page || 1,
                              total: pagination.totalPages || 1,
                            }}
                          />
                        </span>
                        {pagination.page < pagination.totalPages && (
                          <NamedLink
                            name="BlogListPage"
                            to={{ search: `?page=${pagination.page + 1}` }}
                            className={css.paginationButton}
                          >
                            <FormattedMessage id="BlogListPage.next" />
                          </NamedLink>
                        )}
                      </div>
                    )}

                    {posts.length === 0 && !fetchInProgress && (
                      <div className={css.empty}>
                        <FormattedMessage id="BlogListPage.noPosts" />
                      </div>
                    )}
                  </>
                )}
                </section>

                <section className={css.section}>
                  <div className={css.sectionHeader}>
                    <Heading as="h2" rootClassName={css.sectionTitle}>
                      <FormattedMessage id="BlogListPage.pillarsTitle" />
                    </Heading>
                    <p className={css.sectionSubtitle}>
                      <FormattedMessage id="BlogListPage.pillarsSubtitle" />
                    </p>
                  </div>

                  {pillarFetchError ? (
                    <div className={css.inlineError}>
                      <FormattedMessage id="BlogListPage.pillarsError" />
                    </div>
                  ) : null}

                  {pillarFetchInProgress && pillarPages.length === 0 ? (
                    <div className={css.loading}>
                      <FormattedMessage id="BlogListPage.pillarsLoading" />
                    </div>
                  ) : sortedPillarPages.length > 0 ? (
                    <>
                      <div className={css.pillarsGrid}>
                        {visiblePillars.map(page => {
                          if (!page || !page.id) return null;
                          return <BlogCard key={page.id} post={page} />;
                        })}
                      </div>
                      {hasMorePillars && !showAllPillars ? (
                        <div className={css.showMoreContainer}>
                          <button
                            type="button"
                            className={css.showMoreButton}
                            onClick={() => setShowAllPillars(true)}
                          >
                            <FormattedMessage
                              id="BlogListPage.showMorePillars"
                              values={{ count: sortedPillarPages.length - INITIAL_PILLAR_COUNT }}
                            />
                          </button>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className={css.empty}>
                      <FormattedMessage id="BlogListPage.pillarsEmpty" />
                    </div>
                  )}
                </section>
              </main>
              <FooterContainer />
            </>
          )}
        </LayoutComposer>
      </StaticPage>
    </>
  );
};

const mapStateToProps = state => {
  return {
    posts: getBlogPosts(state),
    pillarPages: getPillarPages(state),
    pagination: getBlogPagination(state),
    fetchInProgress: getBlogFetchInProgress(state),
    fetchError: getBlogFetchError(state),
    pillarFetchInProgress: getPillarFetchInProgress(state),
    pillarFetchError: getPillarFetchError(state),
  };
};

const mapDispatchToProps = {
  fetchBlogPosts,
  fetchPillarPages,
};

const BlogListPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withRouter
)(BlogListPageComponent);

export default BlogListPage;

